import {
  EQUIP_SLOTS,
  getItem,
  RARITY_INFO,
  STAT_INFO,
  type EquipSlot,
  type StatBlock,
  type StatKey,
} from '@jianghu/game-data';
import { BAG_SIZE } from '@jianghu/game-rules';
import type { InvItemDTO } from '@jianghu/protocol';
import { useState, type DragEvent } from 'react';
import { gameUi, useGame } from '../../game/store';
import { useGameInstance } from './GameContext';
import { Icon } from './Icon';

const SLOT_LABEL: Record<EquipSlot, string> = {
  weapon: 'Weapon',
  chest: 'Robe',
  head: 'Head',
  feet: 'Feet',
  waist: 'Belt',
  ring: 'Ring',
  neck: 'Neck',
};

function fmtStat(k: StatKey, v: number): string {
  return STAT_INFO[k].percent ? `${(v * 100).toFixed(1)}%` : String(Math.round(v * 10) / 10);
}

export function ItemIcon({ item }: { item: InvItemDTO }) {
  const def = getItem(item.defId);
  return <Icon name={def?.icon ?? 'empty'} color={def?.tint ?? RARITY_INFO[def?.rarity ?? 'common'].color} />;
}

function ItemDetail({ item, equipped }: { item: InvItemDTO; equipped: InvItemDTO | undefined }) {
  const def = getItem(item.defId);
  if (!def) return null;
  const keys = new Set<StatKey>([
    ...(Object.keys(item.stats) as StatKey[]),
    ...(equipped ? (Object.keys(equipped.stats) as StatKey[]) : []),
  ]);
  return (
    <div>
      <div className="nm" style={{ color: RARITY_INFO[def.rarity].color }}>
        {def.name}
      </div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
        {RARITY_INFO[def.rarity].name} {def.equipSlot ? SLOT_LABEL[def.equipSlot] : def.category}
        {def.levelRequired > 1 ? ` · Requires level ${def.levelRequired}` : ''}
      </div>
      {[...keys].map((k) => {
        const v = (item.stats as StatBlock)[k] ?? 0;
        const cur = equipped && equipped.id !== item.id ? ((equipped.stats as StatBlock)[k] ?? 0) : null;
        const diff = cur === null ? 0 : v - cur;
        return (
          <div key={k} className="stat">
            +{fmtStat(k, v)} {STAT_INFO[k].name}
            {cur !== null && diff !== 0 && (
              <span className={diff > 0 ? 'cmp-up' : 'cmp-down'}>
                {' '}
                ({diff > 0 ? '+' : ''}
                {fmtStat(k, diff)})
              </span>
            )}
          </div>
        );
      })}
      <div className="muted" style={{ marginTop: 4 }}>
        {def.description}
      </div>
      {def.sellPrice > 0 && (
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Sells for {def.sellPrice} coins{item.qty > 1 ? ' each' : ''}
        </div>
      )}
    </div>
  );
}

/** Bag + equipment with drag-and-drop, stacking, splitting, use, equip, sell and discard. */
export function InventoryWindow() {
  const game = useGameInstance();
  const open = useGame((s) => s.windows.inventory);
  const items = useGame((s) => s.inventory);
  const coins = useGame((s) => s.self?.coins ?? 0);
  const shop = useGame((s) => s.shop);
  const [sel, setSel] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  if (!open || !game) return null;
  const bag: (InvItemDTO | undefined)[] = Array.from({ length: BAG_SIZE }, (_, i) =>
    items.find((it) => it.bagSlot === i),
  );
  const equipped = (slot: EquipSlot): InvItemDTO | undefined => items.find((it) => it.equipSlot === slot);
  const selected = items.find((i) => i.id === sel);
  const selDef = selected ? getItem(selected.defId) : undefined;

  const onDragStart = (e: DragEvent, it: InvItemDTO): void => {
    e.dataTransfer.setData('text/plain', it.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const dropOnBag = (e: DragEvent, slot: number): void => {
    e.preventDefault();
    setDropTarget(null);
    const id = e.dataTransfer.getData('text/plain');
    const it = items.find((i) => i.id === id);
    if (!it) return;
    if (it.equipSlot) game.invOp({ op: 'unequip', slot: it.equipSlot });
    else if (it.bagSlot !== null && it.bagSlot !== slot)
      game.invOp({ op: 'move', from: it.bagSlot, to: slot });
  };
  const dropOnEquip = (e: DragEvent, slot: EquipSlot): void => {
    e.preventDefault();
    setDropTarget(null);
    const id = e.dataTransfer.getData('text/plain');
    const it = items.find((i) => i.id === id);
    if (!it || it.bagSlot === null) return;
    if (getItem(it.defId)?.equipSlot !== slot) {
      gameUi.toast('error', 'That item does not go in that slot.');
      return;
    }
    game.invOp({ op: 'equip', item: it.id });
  };
  const primary = (it: InvItemDTO): void => {
    const def = getItem(it.defId);
    if (it.equipSlot) game.invOp({ op: 'unequip', slot: it.equipSlot });
    else if (shop && def && def.sellPrice > 0 && shop.buysCategories.includes(def.category))
      game.shopOp(shop.npc, 'sell', it.id, it.qty);
    else if (def?.equipSlot) game.invOp({ op: 'equip', item: it.id });
    else if (def?.use) game.invOp({ op: 'use', item: it.id });
  };

  return (
    <section className="window win-inventory" aria-label="Inventory">
      <div className="window-head">
        <h3>Inventory</h3>
        <span className="coins" title="Coins">
          ◉ {coins}
        </span>
        <button
          className="close-x"
          aria-label="Close inventory"
          onClick={() => gameUi.toggle('inventory', false)}
        >
          ✕
        </button>
      </div>
      <div className="equip-grid" aria-label="Equipment">
        {EQUIP_SLOTS.map((slot) => {
          const it = equipped(slot);
          return (
            <button
              key={slot}
              className={`item-slot ${it ? `rarity-${getItem(it.defId)?.rarity}` : 'empty'} ${dropTarget === `e:${slot}` ? 'drop-target' : ''} ${it && sel === it.id ? 'selected' : ''}`}
              title={SLOT_LABEL[slot]}
              aria-label={`${SLOT_LABEL[slot]}: ${it ? getItem(it.defId)?.name : 'empty'}`}
              draggable={!!it}
              onDragStart={(e) => it && onDragStart(e, it)}
              onDragOver={(e) => {
                e.preventDefault();
                setDropTarget(`e:${slot}`);
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => dropOnEquip(e, slot)}
              onClick={() => it && setSel(it.id)}
              onDoubleClick={() => it && primary(it)}
            >
              {it ? <ItemIcon item={it} /> : <span className="slot-label">{SLOT_LABEL[slot]}</span>}
            </button>
          );
        })}
      </div>
      <div className="bag-grid" aria-label="Bag">
        {bag.map((it, i) => (
          <button
            key={i}
            className={`item-slot ${it ? `rarity-${getItem(it.defId)?.rarity}` : 'empty'} ${dropTarget === `b:${i}` ? 'drop-target' : ''} ${it && sel === it.id ? 'selected' : ''}`}
            aria-label={it ? `${getItem(it.defId)?.name} ×${it.qty}` : `Empty slot ${i + 1}`}
            draggable={!!it}
            onDragStart={(e) => it && onDragStart(e, it)}
            onDragOver={(e) => {
              e.preventDefault();
              setDropTarget(`b:${i}`);
            }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => dropOnBag(e, i)}
            onClick={() => {
              setSel(it?.id ?? null);
              setConfirmDiscard(false);
            }}
            onDoubleClick={() => it && primary(it)}
            onContextMenu={(e) => {
              e.preventDefault();
              if (it) primary(it);
            }}
          >
            {it && <ItemIcon item={it} />}
            {it && it.qty > 1 && <span className="qty">{it.qty}</span>}
          </button>
        ))}
      </div>
      <div className="item-detail">
        {selected ? (
          <>
            <ItemDetail
              item={selected}
              equipped={selDef?.equipSlot ? equipped(selDef.equipSlot) : undefined}
            />
            <div className="item-actions">
              {selected.equipSlot ? (
                <button
                  className="btn btn-sm"
                  onClick={() => game.invOp({ op: 'unequip', slot: selected.equipSlot })}
                >
                  Unequip
                </button>
              ) : (
                <>
                  {selDef?.equipSlot && (
                    <button
                      className="btn btn-sm btn-jade"
                      onClick={() => game.invOp({ op: 'equip', item: selected.id })}
                    >
                      Equip
                    </button>
                  )}
                  {selDef?.use && (
                    <button
                      className="btn btn-sm btn-jade"
                      onClick={() => game.invOp({ op: 'use', item: selected.id })}
                    >
                      Use
                    </button>
                  )}
                  {selected.qty > 1 && selected.bagSlot !== null && (
                    <button
                      className="btn btn-sm"
                      onClick={() =>
                        game.invOp({ op: 'split', slot: selected.bagSlot, qty: Math.floor(selected.qty / 2) })
                      }
                    >
                      Split
                    </button>
                  )}
                  {shop &&
                    selDef &&
                    selDef.sellPrice > 0 &&
                    shop.buysCategories.includes(selDef.category) && (
                      <button
                        className="btn btn-sm"
                        onClick={() => game.shopOp(shop.npc, 'sell', selected.id, selected.qty)}
                      >
                        Sell ({selDef.sellPrice * selected.qty})
                      </button>
                    )}
                  {confirmDiscard ? (
                    <>
                      <span className="muted">Destroy?</span>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          game.invOp({ op: 'discard', item: selected.id });
                          setConfirmDiscard(false);
                          setSel(null);
                        }}
                      >
                        Yes
                      </button>
                      <button className="btn btn-sm" onClick={() => setConfirmDiscard(false)}>
                        No
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-ghost" onClick={() => setConfirmDiscard(true)}>
                      Discard
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <span className="muted">
            Select an item. Drag to rearrange or equip; double-click or right-click to use / equip
            {shop ? ' / sell' : ''}.
          </span>
        )}
      </div>
    </section>
  );
}
